import { configs } from "./configs";

const COMMONS_API = "https://commons.wikimedia.org/w/api.php";

export interface WikimediaSearchOptions {
	limit?: number;
	offset?: number;
}

export interface WikimediaAsset {
	id: string;
	fileUrl: string;
	fileType: "image" | "video";
	width: number;
	height: number;
	duration?: number;
	title: string;
	attribution: string;
	sourcePageUrl: string;
	mime?: string;
}

interface CommonsImageInfo {
	url?: string;
	descriptionurl?: string;
	width?: number;
	height?: number;
	size?: number;
	mime?: string;
	thumburl?: string;
	user?: string;
	extmetadata?: Record<string, { value?: string }>;
}

interface CommonsPage {
	pageid: number;
	title: string;
	snippet?: string;
	imageinfo?: CommonsImageInfo[];
}

interface CommonsSearchResponse {
	query?: {
		pages?: Record<string, CommonsPage>;
	};
}

function getUserAgent(): string {
	return configs.wikimedia?.userAgent || "SwiftiaVideoProject/1.0";
}

function filetypeFilter(type: "image" | "video"): string {
	return type === "video" ? "video" : "bitmap|drawing";
}

function isLikelyVideoMime(mime?: string): boolean {
	if (!mime) return false;
	return mime.startsWith("video/");
}

function attributionFromPage(page: CommonsPage, info: CommonsImageInfo): string {
	const artist =
		info.extmetadata?.Artist?.value?.replace(/<[^>]+>/g, "").trim() ||
		info.user ||
		"Wikimedia Commons";
	const title = page.title.replace(/^File:/, "");
	return `${title} — ${artist} (Wikimedia Commons)`;
}

function pageToAsset(
	page: CommonsPage,
	preferredType: "image" | "video",
): WikimediaAsset | null {
	const info = page.imageinfo?.[0];
	if (!info?.url) return null;

	const mime = info.mime || "";
	const isVideo = isLikelyVideoMime(mime);
	if (preferredType === "image" && isVideo) return null;
	if (preferredType === "video" && !isVideo) return null;

	const width = info.width || 0;
	const height = info.height || 0;
	if (preferredType === "image" && (width < 400 || height < 400)) return null;

	return {
		id: `wikimedia_${page.pageid}`,
		fileUrl: info.url,
		fileType: isVideo ? "video" : "image",
		width,
		height,
		title: page.title.replace(/^File:/, ""),
		attribution: attributionFromPage(page, info),
		sourcePageUrl: info.descriptionurl || "",
		mime,
	};
}

/**
 * Search Wikimedia Commons for images or videos.
 */
export async function searchWikimediaAssets(
	query: string,
	type: "image" | "video",
	options?: WikimediaSearchOptions,
): Promise<WikimediaAsset[]> {
	const trimmed = query.trim();
	if (!trimmed) return [];

	const limit = Math.min(40, Math.max(1, options?.limit ?? 15));
	const offset = Math.max(0, options?.offset ?? 0);
	const params = new URLSearchParams({
		action: "query",
		format: "json",
		origin: "*",
		uselang: "en",
		generator: "search",
		gsrnamespace: "6",
		gsrprop: "snippet",
		gsrlimit: String(limit),
		gsroffset: String(offset),
		gsrsearch: `filetype:${filetypeFilter(type)} ${trimmed}`,
		prop: "info|imageinfo",
		iiprop: "url|size|mime|user|extmetadata",
		iiurlheight: "240",
	});

	const response = await fetch(`${COMMONS_API}?${params.toString()}`, {
		headers: {
			"User-Agent": getUserAgent(),
			Accept: "application/json",
		},
	});

	if (!response.ok) {
		throw new Error(`Wikimedia Commons API error: ${response.status}`);
	}

	const data = (await response.json()) as CommonsSearchResponse;
	const pages = Object.values(data.query?.pages || {});
	const assets: WikimediaAsset[] = [];
	for (const page of pages) {
		const asset = pageToAsset(page, type);
		if (asset) assets.push(asset);
	}
	return assets;
}
